import { PageHeader } from '@/components/PageHeader/PageHeader';
import { SidebarNav } from '@/components/SidebarNav/SidebarNav';
import { SidebarNavItem } from '@/components/SidebarNav/components/SidebarNavItem';
import { AgentsActivityNavGroup } from '@/components/SidebarNav/components/AgentsActivityNavGroup';
import { ShadowAINavGroup } from '@/components/SidebarNav/components/ShadowAINavGroup';
import { PoliciesNavGroup } from '@/components/SidebarNav/components/PoliciesNavGroup';
import { AiAppSecNavGroup } from '@/components/SidebarNav/components/AiAppSecNavGroup';
import { policiesPageTitle } from '@/lib/policyPaths';
import {
  Bell,
  Shield,
  Download,
  Settings,
  AlertTriangle,
  ShieldCheck,
  ShieldPlus,
  Ghost,
  Package,
  type LucideIcon,
} from 'lucide-react';
import { type ComponentType } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useLayoutWithSidebar } from '@/hooks/useLayout';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { ExtensionScanResultPage } from '@/pages/shared/ExtensionScanResultPage';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { isExtensionScanResultFromAlerts } from '@/components/grouped-alerts/extension-scan-result-link';
import { getSubheaderForTab, UserTab, type UserTabType } from './constants';
import PoliciesTab from './tabs/PoliciesTab';
import SecureAiCodingTab from './tabs/SecureAiCodingTab';
import ThreatModelingTab from './tabs/ThreatModelingTab';
import { LlmVulnDiscoveryPage } from '@/components/LlmVulnDiscovery/LlmVulnDiscoveryPage';
import SettingsTab from './tabs/SettingsTab';
import DataTableMCPTab from './tabs/DataTableMCPTab';
import ShadowAITab from './tabs/ShadowAITab';
import InstallTab from '@/components/InstallTab/InstallTab';
import AttackDemonstrationsTab from '@/components/AttackDemonstrationsTab';
import { useReorderTabsOnSetup } from '@/hooks/useReorderTabsOnSetup';
import { Loading } from '@/components/shared/placeholders/Loading';
import { SharedSidebar } from '@/components/Sidebar';
import { PageContentRoot } from '@/components/shared/layout/PageContentRoot';
import { PostureManagement } from '@/components/Inventory';
import ExtensionsTab from './tabs/ExtensionsTab';
import PluginsTab from './tabs/PluginsTab';
import GroupAlertsTab from './tabs/GroupAlertsTab';
import AgentsActivityTab from '../organization/tabs/AgentsActivityTab';

/**
 * Tab configuration types
 */
type TabComponentType = ComponentType<unknown>;
type UserTabConfig = Record<
  UserTabType,
  {
    label: string;
    icon: LucideIcon;
    component: TabComponentType;
  }
>;

/**
 * Tab configuration: labels, icons, and components
 */
const TAB_CONFIG: UserTabConfig = {
  [UserTab.Install]: {
    label: 'Get Started',
    icon: Download,
    component: InstallTab as TabComponentType,
  },
  [UserTab.Alerts]: {
    label: 'Alerts',
    icon: Bell,
    component: GroupAlertsTab,
  },
  [UserTab.Policies]: {
    label: 'Policies',
    icon: Shield,
    component: PoliciesTab,
  },
  [UserTab.SecureAiCoding]: {
    label: 'Secure AI Coding Enforcement',
    icon: ShieldPlus,
    component: SecureAiCodingTab,
  },
  [UserTab.ThreatModeling]: {
    label: 'Threat Modeling',
    icon: ShieldPlus,
    component: ThreatModelingTab,
  },
  [UserTab.LlmVulnDiscovery]: {
    label: 'Vulnerability Discovery with LLM',
    icon: ShieldPlus,
    component: LlmVulnDiscoveryPage,
  },
  [UserTab.PostureManagement]: {
    label: 'Inventory',
    icon: ShieldCheck,
    component: PostureManagement as TabComponentType,
  },
  [UserTab.ShadowAI]: {
    label: 'Shadow AI',
    icon: Ghost,
    component: ShadowAITab,
  },
  [UserTab.AgentsActivity]: {
    label: 'Packages & Repos',
    icon: Package,
    component: AgentsActivityTab as TabComponentType,
  },
  [UserTab.Settings]: {
    label: 'Settings',
    icon: Settings,
    component: SettingsTab,
  },
  [UserTab.AttackDemonstrations]: {
    label: 'Attack Demonstrations',
    icon: AlertTriangle,
    component: AttackDemonstrationsTab,
  },
};

/**
 * Build full path for a user tab.
 *
 * ⚠️ KIR-2659 deep-link contract: when `tab === UserTab.Alerts` this
 * returns `/dashboard/alerts`, which the desktop agent and the IDE
 * extensions hard-code as the personal-account fallback in their
 * summary-banner URL builders (`buildSummaryDeepLinkURL` in
 * extensions/agent/pkg/notifications/coordinator.go and
 * extensions/vscode-core/src/services/notifications/coordinator.ts —
 * search for `user-alt`). Changing the `/dashboard/` prefix here or
 * the `'alerts'` string in `UserTab.Alerts` silently breaks every
 * notification deep-link to the personal alerts view. Update the
 * agent + extension URL builders in the same PR.
 */
function buildUserPath(tab: string): string {
  return `/dashboard/${tab}`;
}

const createSideNavItems = (
  tabConfig: Partial<UserTabConfig>,
  isShadowAiEnabled: boolean,
  isAgentCommandVisibilityEnabled: boolean,
  isSecureAiCodingEnabled: boolean,
  isAiAgentInstructionsEnabled: boolean
) => {
  return Object.keys(tabConfig).map(tab => {
    const isEmptyTab = !tabConfig[tab as UserTabType];
    if (isEmptyTab) return null;

    // PostureManagement is always visible (graduated feature)

    const disableShadowAI = tab === UserTab.ShadowAI && !isShadowAiEnabled;
    if (disableShadowAI) {
      return null;
    }
    const disableAgentsActivity =
      tab === UserTab.AgentsActivity && !isAgentCommandVisibilityEnabled;
    if (disableAgentsActivity) {
      return null;
    }
    // KIR-3925: the AI AppSec section renders at the SecureAiCoding slot below;
    // its two extra tabs never render as standalone sidebar items.
    if (tab === UserTab.ThreatModeling || tab === UserTab.LlmVulnDiscovery) {
      return null;
    }
    const tabConfigEntry = tabConfig[tab as UserTabType];
    if (!tabConfigEntry) {
      return null;
    }
    const { label, icon: Icon } = tabConfigEntry;
    // KIR-3816 / KIR-3925: the "AI AppSec" section groups Secure AI Coding
    // Enforcement, Threat Modeling, and Vulnerability Discovery — all riding
    // the ai_instruction_catalog flag.
    if (tab === UserTab.SecureAiCoding) {
      if (!isSecureAiCodingEnabled) {
        return null;
      }
      return (
        <AiAppSecNavGroup
          key={tab}
          icon={Icon}
          secureAiCodingTo={buildUserPath(UserTab.SecureAiCoding)}
          threatModelingTo={buildUserPath(UserTab.ThreatModeling)}
          vulnerabilityDiscoveryTo={buildUserPath(UserTab.LlmVulnDiscovery)}
        />
      );
    }
    const isAgentsActivity = tab === UserTab.AgentsActivity;
    if (isAgentsActivity) {
      return <AgentsActivityNavGroup key={tab} to={buildUserPath(tab)} />;
    }
    const isShadowAI = tab === UserTab.ShadowAI;
    if (isShadowAI) {
      // Personal accounts have no Network Discovery page, so the group shows
      // only the "Tools" child (title reads "Shadow AI / Tools").
      return (
        <ShadowAINavGroup
          key={tab}
          to={buildUserPath(tab)}
          icon={Icon}
          showNetworkDiscovery={false}
        />
      );
    }
    const isPolicies = tab === UserTab.Policies;
    if (isPolicies) {
      // KIR-3982: Policies splits into "Detection Policies" and
      // "Allow / Block Lists" sub-items, mirroring the Shadow AI group.
      return (
        <PoliciesNavGroup
          key={tab}
          to={buildUserPath(tab)}
          icon={Icon}
          showAiInstructions={isAiAgentInstructionsEnabled}
        />
      );
    }
    return (
      <SidebarNavItem
        key={tab}
        to={buildUserPath(tab)}
        icon={<Icon className="h-4 w-4" />}
        label={label}
      />
    );
  });
};

export default function Dashboard() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { flags, isLoading: isLoadingFlags } = useFeatureFlags([
    flagsmithFeatureFlags.SHADOW_AI,
    flagsmithFeatureFlags.AGENT_COMMAND_VISIBILITY,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG,
    flagsmithFeatureFlags.HOOK_INVENTORY_ENABLED,
  ]);
  const isShadowAiEnabled = flags[flagsmithFeatureFlags.SHADOW_AI] || false;
  // KIR-3982: the `ai-instruction-policies` group is registered behind
  // `ai_instruction_catalog` (not `ai_rules_policy`), so its sub-page follows it.
  const isAiAgentInstructionsEnabled =
    flags[flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG] || false;
  // KIR-3643: personal Hooks inventory tab visibility.
  const isHookInventoryEnabled =
    flags[flagsmithFeatureFlags.HOOK_INVENTORY_ENABLED] || false;
  const isAgentCommandVisibilityEnabled =
    flags[flagsmithFeatureFlags.AGENT_COMMAND_VISIBILITY] || false;
  const isCapabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  // KIR-3925: also gates the Threat Modeling + LLM Vulnerability Discovery
  // pages (the whole AI AppSec section rides this one flag).
  const isSecureAiCodingEnabled =
    flags[flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG] || false;

  const { tabConfig, isLoading, isSetup } = useReorderTabsOnSetup({
    tabs: TAB_CONFIG,
    selectorKey: UserTab.Install,
    installAfterKey: UserTab.AttackDemonstrations,
  });

  // Register that this page has a sidebar (handles cleanup automatically)
  useLayoutWithSidebar();

  // Determine current tab from URL path (strip trailing slashes first)
  const pathname = location.pathname.replace(/\/+$/, '');

  // Handle nested routes like /dashboard/alerts/:alertKey/occurrences, and
  // scan-result deep links opened from Alerts (path stays under /inventory/).
  let currentTab: UserTabType;
  if (
    pathname.includes('/alerts') ||
    isExtensionScanResultFromAlerts(pathname, searchParams)
  ) {
    currentTab = UserTab.Alerts;
  } else {
    // Skip the leading "dashboard" segment; the tab slot is the segment
    // directly under the scope. Match that segment explicitly first so a future
    // nested sub-slug (e.g. /dashboard/policies/extension-security-catalog) can
    // never flip tab detection. Fall back to scanning the remaining segments
    // for a known tab (covers deeper nesting like /dashboard/alerts/... handled
    // above), then Install ("Get Started").
    const segments = pathname.split('/').filter(Boolean).slice(1);
    const tabValues = Object.values(UserTab);
    currentTab =
      tabValues.find(tab => tab === segments[0]) ??
      tabValues.find(tab => segments.includes(tab)) ??
      UserTab.Install;
  }

  const isTabDisabledByFlag =
    (currentTab === UserTab.ShadowAI && !isShadowAiEnabled) ||
    (currentTab === UserTab.AgentsActivity &&
      !isAgentCommandVisibilityEnabled) ||
    ((currentTab === UserTab.SecureAiCoding ||
      currentTab === UserTab.ThreatModeling ||
      currentTab === UserTab.LlmVulnDiscovery) &&
      !isSecureAiCodingEnabled);

  if (isTabDisabledByFlag) {
    currentTab = isSetup ? UserTab.Alerts : UserTab.Install;
  }

  // Shadow AI has only the Tools page here. Policies (KIR-3982) reflects its
  // sub-page in the title, leaving catalog sub-routes as plain "Policies".
  const currentPageTitle =
    currentTab === UserTab.ShadowAI
      ? 'Shadow AI / Tools'
      : currentTab === UserTab.Policies
        ? policiesPageTitle(pathname) || tabConfig?.[currentTab]?.label || ''
        : tabConfig?.[currentTab]?.label || '';
  const currentPageIcon = tabConfig?.[currentTab]?.icon;
  const currentPageSubheader = getSubheaderForTab(currentTab);
  const initialPage = buildUserPath(isSetup ? UserTab.Alerts : UserTab.Install);
  const tabsReady = !!tabConfig;

  // Set browser tab title
  useDocumentTitle(currentPageTitle);

  return (
    <>
      <SharedSidebar>
        <SidebarNav>
          {isLoading
            ? null
            : createSideNavItems(
                tabConfig,
                isShadowAiEnabled,
                isAgentCommandVisibilityEnabled,
                isSecureAiCodingEnabled,
                isAiAgentInstructionsEnabled
              )}
        </SidebarNav>
      </SharedSidebar>

      <PageContentRoot>
        {isLoading ? (
          <Loading label="Loading" />
        ) : tabsReady ? (
          <>
            <PageHeader
              title={currentPageTitle}
              icon={currentPageIcon}
              subheader={currentPageSubheader}
            />

            {/* Page Content */}
            <main className="flex-1 p-6 pb-20 w-full min-w-[320px]">
              <Routes>
                {/* /dashboard → redirect to install tab */}
                <Route
                  index
                  element={<Navigate to={`${initialPage}`} replace />}
                />

                {/* Backwards-compat redirect: /posture-management → /inventory */}
                <Route
                  path="posture-management"
                  element={<Navigate to="/dashboard/inventory" replace />}
                />

                {/* /dashboard/* - tab routes (generated from config) */}
                {Object.entries(TAB_CONFIG).map(([tab, config]) => {
                  // PostureManagement is always visible (graduated feature)

                  const hideShadowAI =
                    tab === UserTab.ShadowAI && !isShadowAiEnabled;
                  if (hideShadowAI) {
                    return null;
                  }
                  const hideAgentsActivity =
                    tab === UserTab.AgentsActivity &&
                    !isAgentCommandVisibilityEnabled;
                  if (hideAgentsActivity) {
                    return null;
                  }
                  // KIR-3816 / KIR-3925: all three AI AppSec pages follow the
                  // ai_instruction_catalog flag.
                  const hideAiAppSec =
                    (tab === UserTab.SecureAiCoding ||
                      tab === UserTab.ThreatModeling ||
                      tab === UserTab.LlmVulnDiscovery) &&
                    !isSecureAiCodingEnabled;
                  if (hideAiAppSec) {
                    return null;
                  }
                  const Component = config.component;

                  const isPostureManagementTab =
                    tab === UserTab.PostureManagement;
                  const element = isPostureManagementTab ? (
                    <PostureManagement
                      hookInventoryAllowed={isHookInventoryEnabled}
                      MCPTabSlot={<DataTableMCPTab />}
                      ExtensionsTabSlot={<ExtensionsTab />}
                      PluginsTabSlot={<PluginsTab />}
                    />
                  ) : (
                    <Component />
                  );
                  return (
                    // ⚠️ KIR-2659 deep-link contract: the personal
                    // alerts subroute `/dashboard/alerts` is referenced
                    // verbatim by the agent + IDE extension when they
                    // build summary-banner "View Details" deep links
                    // (extensions/agent/pkg/notifications/coordinator.go
                    // and .../vscode-core/.../coordinator.ts —
                    // buildSummaryDeepLinkURL falls back here when no
                    // org context exists). Renaming the `'alerts/*'`
                    // path segment breaks every notification deep-link
                    // to the personal view; update the URL builders in
                    // the same PR.
                    <Route
                      key={tab}
                      path={
                        tab === UserTab.Alerts
                          ? 'alerts/*'
                          : tab === UserTab.Policies
                            ? 'policies/*'
                            : tab
                      }
                      element={element}
                    />
                  );
                })}

                <Route
                  path="inventory/extensions/:extensionId/scan-result"
                  element={
                    isLoadingFlags ? null : isCapabilityDetectionEnabled ? (
                      <ExtensionScanResultPage />
                    ) : (
                      <Navigate to="/dashboard/inventory" replace />
                    )
                  }
                />

                {/* Catch-all - redirect to dashboard root */}
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>
            </main>
          </>
        ) : null}
      </PageContentRoot>
    </>
  );
}
